import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { ElementType, Ref, PointerEvent, KeyboardEvent } from 'react';

import { cn } from '@/lib/utils/tailwind';

type RadixStateProps = {
  'data-state'?: 'open' | 'closed';
};

export type NavMenuProps = Omit<
  NavigationMenu.NavigationMenuProps,
  'ref' | 'color'
> & {
  ref?: Ref<HTMLDivElement>;
};
export type NavMenuListProps = Omit<
  NavigationMenu.NavigationMenuListProps,
  'ref' | 'color'
> & {
  ref?: Ref<HTMLUListElement>;
};
export type NavMenuItemProps = Omit<
  NavigationMenu.NavigationMenuItemProps,
  'color'
> & {
  ref?: Ref<HTMLLIElement>;
};
export type NavMenuLinkProps = Omit<
  NavigationMenu.NavigationMenuLinkProps,
  'ref' | 'color' | 'onSelect'
> & {
  ref?: Ref<HTMLAnchorElement>;
  onSelect?: (
    event: PointerEvent<HTMLAnchorElement> | KeyboardEvent<HTMLAnchorElement>,
  ) => void;
};
export type NavMenuTriggerProps<T extends ElementType = 'button'> = Omit<
  NavigationMenu.NavigationMenuTriggerProps,
  'color' | 'asChild'
> &
  RadixStateProps & {
    as?: T;
    ref?: Ref<HTMLButtonElement>;
  };
export type NavMenuContentProps = Omit<
  NavigationMenu.NavigationMenuContentProps,
  'color'
> & {
  ref?: Ref<HTMLDivElement>;
};
export type NavMenuSubProps = Omit<
  NavigationMenu.NavigationMenuSubProps,
  'color' | 'defaultValue'
> & {
  defaultValue?: string;
  ref?: Ref<HTMLDivElement>;
};
export type NavMenuIndicatorProps = Omit<
  NavigationMenu.NavigationMenuIndicatorProps,
  'color'
> & {
  ref?: Ref<HTMLDivElement>;
};
export type NavMenuViewportProps = Omit<
  NavigationMenu.NavigationMenuViewportProps,
  'color'
> & {
  ref?: Ref<HTMLDivElement>;
};

const Root = ({ className, children, ref, ...props }: NavMenuProps) => (
  <NavigationMenu.Root className={cn(className)} ref={ref} {...props}>
    {children}
  </NavigationMenu.Root>
);

const List = ({ className, children, ...props }: NavMenuListProps) => (
  <NavigationMenu.List className={cn(className)} {...props}>
    {children}
  </NavigationMenu.List>
);

const Item = ({ className, children, ref, ...props }: NavMenuItemProps) => (
  <NavigationMenu.Item className={cn(className)} ref={ref} {...props}>
    {children}
  </NavigationMenu.Item>
);

const Trigger = <T extends ElementType = 'button'>({
  as,
  className,
  children,
  ref,
  ...props
}: NavMenuTriggerProps<T>) => {
  const Component = as || 'button';

  return (
    <NavigationMenu.Trigger asChild>
      <Component
        className={cn(className)}
        role="button"
        aria-haspopup="menu"
        aria-expanded={!!props['data-state'] && props['data-state'] === 'open'}
        ref={ref}
        {...props}
      >
        {children}
      </Component>
    </NavigationMenu.Trigger>
  );
};

const Content = ({
  className,
  children,
  ref,
  ...props
}: NavMenuContentProps) => (
  <NavigationMenu.Content className={cn(className)} ref={ref} {...props}>
    {children}
  </NavigationMenu.Content>
);

const Link = ({
  className,
  children,
  onSelect,
  ref,
  ...props
}: NavMenuLinkProps) => {
  return (
    <NavigationMenu.Link
      className={cn(className)}
      ref={ref}
      onPointerDown={onSelect}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          onSelect?.(event);
        }
      }}
      {...props}
    >
      {children}
    </NavigationMenu.Link>
  );
};

const Sub = ({ className, children, ref, ...props }: NavMenuSubProps) => (
  <NavigationMenu.Sub className={cn(className)} ref={ref} {...props}>
    {children}
  </NavigationMenu.Sub>
);

const Indicator = ({
  className,
  children,
  ref,
  ...props
}: NavMenuIndicatorProps) => (
  <NavigationMenu.Indicator className={cn(className)} ref={ref} {...props}>
    {children}
  </NavigationMenu.Indicator>
);

const Viewport = ({
  className,
  children,
  ref,
  ...props
}: NavMenuViewportProps) => (
  <NavigationMenu.Viewport className={cn(className)} ref={ref} {...props}>
    {children}
  </NavigationMenu.Viewport>
);

const subComponents = {
  List,
  Item,
  Trigger,
  Content,
  Link,
  Sub,
  Indicator,
  Viewport,
};

const NavMenu = Object.assign(Root, subComponents);

export { NavMenu };
